<template>
  <v-container fluid class="pa-6">
    <div class="d-flex justify-space-between align-center mb-6">
      <h1 class="text-h5 font-weight-bold text-grey-darken-4">Lista de Empresas</h1>
      <!--<v-btn color="primary" @click="handleCreateEnterprise">
        <v-icon start>mdi-plus</v-icon>
        Adicionar Nova Empresa
      </v-btn>-->
    </div>

    <v-card class="elevation-4">
      <v-card-text>
        <v-row class="mb-6" dense>
          <v-col cols="12" md="8">
            <v-text-field v-model="searchTerm" placeholder="Pesquisar por nome ou CNPJ" clearable
              hide-details prepend-inner-icon="mdi-magnify" @input="onFiltersChange" />
          </v-col>
          <v-col cols="12" md="4">
            <v-select v-model="selectedIsActive" :items="activeOptions" placeholder="Filtrar por Status" clearable
              hide-details @update:model-value="onFiltersChange" />
          </v-col>
        </v-row>

        <v-data-table-server :headers="columns" :items="enterprises" :items-length="totalItems" :loading="isLoading"
          :items-per-page="itemsPerPage" :page="page" :no-data-text="noDataMessage" class="elevation-0"
          @update:page="page = $event" @update:items-per-page="itemsPerPage = $event">

          <template v-slot:item.is_active="{ item }">
            <v-chip :color="isActiveTagMap[item.is_active]" label>
              {{ formatIsActive(item.is_active) }}
            </v-chip>
          </template>

          <template v-slot:item.actions="{ item }">
            <v-icon icon="mdi-pencil" color="warning" size="large" class="mr-2" @click="handleEdit(item)"
              title="Editar" />
            <!--<v-icon icon="mdi-delete" color="error" size="large" @click="handleDelete(item)" title="Excluir" />-->
          </template>
        </v-data-table-server>

        <v-alert v-if="hasError && !isLoading" type="error" variant="tonal" class="mt-4 text-center">
          Ocorreu um erro ao carregar os dados das empresas.
        </v-alert>
      </v-card-text>
    </v-card>

    <v-dialog v-model="deleteDialog" max-width="500">
      <v-card>
        <v-card-title class="text-h5">Confirmar Exclusão</v-card-title>
        <v-card-text>
          Tem certeza de que deseja excluir a empresa
          <strong>{{ itemToDelete.name }}</strong> (ID: <strong>{{ itemToDelete.id }}</strong>)?
          Essa ação pode afetar clientes e usuários vinculados.
        </v-card-text>
        <v-card-actions class="d-flex justify-space-between">
          <v-btn color="secondary" variant="flat" @click="deleteDialog = false">
            Cancelar
          </v-btn>
          <v-btn color="error" variant="flat" @click="confirmDelete">
            Excluir
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

  </v-container>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useRouter } from 'vue-router'
import api from '@/services/api'
import { useToastStore } from '@/store/toast'
import { useLoadingStore } from '@/store/loadingStore'

const router = useRouter()
const toastStore = useToastStore()
const loadingStore = useLoadingStore()

// Estado
const enterprises = ref([])
const isLoading = ref(true)
const hasError = ref(false)
const searchTerm = ref('')
const selectedIsActive = ref(null)

// Paginação
const page = ref(1)
const itemsPerPage = ref(10)
const totalItems = ref(0)

// Exclusão
const deleteDialog = ref(false)
const itemToDelete = ref({})

// Opções de Filtro
const activeOptions = [
  { title: 'Ativo', value: '1' },
  { title: 'Inativo', value: '0' }
]

// Mapeamentos de Cores
const isActiveTagMap = {
  0: 'error',
  1: 'success'
}

const noDataMessage = computed(() => {
  if (isLoading.value) return 'Carregando...'
  return 'Nenhuma empresa encontrada.'
})

const columns = [
  { title: 'ID', key: 'id', sortable: true },
  { title: 'Nome', key: 'name', sortable: true },
  { title: 'Documento (CNPJ)', key: 'document', sortable: false },
  { title: 'Email', key: 'email', sortable: false },
  { title: 'Ativo', key: 'is_active', sortable: true },
  { title: 'Ações', key: 'actions', sortable: false }
]

const loadEnterprises = async () => {
  isLoading.value = true
  hasError.value = false
  loadingStore.show()
  try {
    const params = {
      page: page.value,
      per_page: itemsPerPage.value,
      search: searchTerm.value, 
      is_active: selectedIsActive.value
    }

    const response = await api.get('/enterprises', { params })

    enterprises.value = response.data.data
    totalItems.value = response.data.total
    itemsPerPage.value = response.data.per_page
    page.value = response.data.current_page

  } catch (error) {
    console.error('Erro ao carregar empresas:', error)
    hasError.value = true
    toastStore.showToast({
      message: error?.response?.data?.error || 'Erro ao carregar a lista de empresas.',
      color: 'error'
    })
  } finally {
    isLoading.value = false
    loadingStore.hide()
  }
}

watch([page, itemsPerPage], () => {
  loadEnterprises()
})

const onFiltersChange = () => {
  page.value = 1
  loadEnterprises()
}

const handleCreateEnterprise = () => {
  router.push('/app/empresas/novo') 
}

const handleEdit = async (row) => {
  router.push({ name: 'enterprise-edit', params: { id: row.id } })
}

const handleDelete = (item) => {
  itemToDelete.value = item
  deleteDialog.value = true
}

const confirmDelete = async () => {
  deleteDialog.value = false
  try {
    await api.delete(`/enterprises/${itemToDelete.value.id}`)
    toastStore.showToast({
      message: 'Empresa excluída com sucesso!',
      color: 'success'
    })

    await loadEnterprises()
  } catch (error) {
    console.error('Erro ao excluir empresa:', error)
    toastStore.showToast({
      message: error?.response?.data?.error || 'Erro ao excluir empresa. Verifique se existem dependências.',
      color: 'error'
    })
  }
}

function formatIsActive(is_active) {
  return is_active === 1 ? 'Ativa' : 'Inativa'
}

onMounted(() => {
  loadEnterprises()
})
</script>

<style scoped>
/* Estilos mantidos do modelo */
</style>